import loadable from "@loadable/component";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ErrorBoundary from "@Components/ErrorBoundary";
import FullPageLoader from "@Components/FullPageLoader";
import NotFound from "@Pages/NotFound";
import type { AppState } from "@Store/index";
import { getBlogLanguageFromPath } from "@Pages/Blog/utils/blogLanguage";
import isBlogRevampEnabled from "@Pages/Blog/utils/isBlogRevampEnabled";
import { STAGE_QUERY_PARAM } from "@Pages/Blog/BlogHomeRevamp/settings";
import getBlogRegionData from "./data/getBlogRegionData";
import loadData from "../data/loadData";

const BlogRegion = loadable(() => import("./BlogRegion"), {
  fallback: <FullPageLoader />,
});

/**
 * Region PDP shell — same pattern as BlogCategory: loadData validates CMS + sets Redux;
 * UI reads denied/ready flags only (no slug-list gate in the route wrapper).
 */
export default (props: any): JSX.Element => {
  const dispatch = useDispatch();
  const slug: string | undefined = props?.match?.params?.slug;
  const isEnabled = isBlogRevampEnabled(getBlogLanguageFromPath(props?.location?.pathname ?? ""));
  const { deniedSlug, readySlug, region } = useSelector(({ blogHomeData }: AppState) => ({
    deniedSlug: blogHomeData.revampRegionPageDeniedSlug,
    readySlug: blogHomeData.revampRegionPageReadySlug,
    region: blogHomeData.revampRegionPageRegion,
  }));

  useEffect(() => {
    if (!__CLIENT__ || !isEnabled || !slug) return;
    const stage = new URLSearchParams(window.location.search).get(STAGE_QUERY_PARAM);
    const skipRegionLiveGate = readySlug === slug;
    dispatch(getBlogRegionData({ slug, stage, isEnabled, skipRegionLiveGate }));
  }, [dispatch, slug, isEnabled, readySlug, props?.location?.search]);

  if (!isEnabled || !slug) {
    return <NotFound staticContext={props?.staticContext} />;
  }

  if (deniedSlug === slug) {
    return <NotFound staticContext={props?.staticContext} />;
  }

  if (__CLIENT__ && (readySlug !== slug || !region)) {
    return <FullPageLoader />;
  }

  if (!region) {
    return <FullPageLoader />;
  }

  return (
    <ErrorBoundary>
      <BlogRegion region={region} />
    </ErrorBoundary>
  );
};

export { loadData };
